import { getTimelineTypeConfig } from "../../services/timeline/constants";
import { archiveChild } from "../../services/childService";
import { CARE_PROFILE_ROUTE } from "../../constants/profileRoutes";

export const usePanelDashboardActions = ({
  navigate,
  refreshRoles,
  setCurrentChildId,
  setDailyCareAction,
  setDailyCareChild,
  setFoodLogChild,
  setIncidentChild,
  setShowDailyCareModal,
  setShowFoodLogSheet,
  setShowIncidentModal,
  setShowSleepLogSheet,
  setSleepLogChild,
  setSleepLogType,
  theme,
}) => {
  const handleInviteTeamMember = (childId) => {
    navigate('/care-team', { state: { focusChildId: childId || null } });
  };

  const handleAddChild = () => {
    navigate('/children/new');
  };

  const handleEditChild = (child, options = {}) => {
    if (!child?.id) {
      return;
    }

    setCurrentChildId(child.id);
    try {
      window.sessionStorage.setItem("lifelog:last-edit-child-id", child.id);
    } catch (error) {
      // Ignore storage failures; route state still carries child id.
    }

    navigate(CARE_PROFILE_ROUTE, {
      state: {
        childId: child.id,
        initialStep: Number.isFinite(Number(options.initialStep))
          ? Number(options.initialStep)
          : 0,
        ...(options.openMedicationManager ? {
          openChildMedicationManager: {
            childId: child.id,
          },
        } : {}),
      },
    });
  };

  const handleReviewChild = (child) => {
    if (!child?.id) {
      return;
    }

    setCurrentChildId(child.id);
    try {
      window.sessionStorage.setItem("lifelog:last-edit-child-id", child.id);
    } catch (error) {
      // Ignore storage failures; route state still carries child id.
    }

    navigate(CARE_PROFILE_ROUTE, {
      state: {
        childId: child.id,
      },
    });
  };

  const handleDeleteChild = async (child) => {
    if (!child?.id) {
      return;
    }

    const confirmed = window.confirm(`Archive ${child.name}?`);
    if (!confirmed) {
      return;
    }

    try {
      await archiveChild(child.id);
      await refreshRoles();
    } catch (error) {
      console.error("Error archiving child:", error);
      alert(error?.message || "Unable to archive child right now.");
    }
  };

  const handleAddChildSuccess = async () => {
    await refreshRoles?.();
  };

  const handleEditChildSuccess = async () => {
    await refreshRoles?.();
  };

  const handleShowCareReport = (child) => {
    if (!child?.id) {
      return;
    }

    setCurrentChildId(child.id);
    navigate('/therapy-prep');
  };

  const handleDailyReport = (child) => {
    handleShowCareReport(child);
  };

  const handleCreateCustomCategories = (categories) => {
    console.log('Creating custom categories:', categories);
  };

  const handleMessages = async (child) => {
    if (!child?.id) {
      return;
    }

    setCurrentChildId(child.id);

    const searchParams = new URLSearchParams({
      childId: child.id,
      openChildChat: '1',
      returnToDashboard: '1',
    });

    navigate(`/messages?${searchParams.toString()}`, {
      state: {
        selectedChildId: child.id,
        openChildChat: true,
        returnToDashboard: true,
      }
    });
  };

  const refreshDashboard = async () => {
    await refreshRoles();
  };

  const handleGroupActionClick = (action, child) => {
    setCurrentChildId(child.id);

    if (action.key === "sleep") {
      setSleepLogChild(child);
      setSleepLogType('night');
      setShowSleepLogSheet(true);
      return;
    }

    if (["mood", "food_health", "safety"].includes(action.key)) {
      setDailyCareAction(action.key);
      setDailyCareChild(child);
      setShowDailyCareModal(true);
      return;
    }

    if (action.key === "incident") {
      setIncidentChild(child);
      setShowIncidentModal(true);
      return;
    }

    if (action.key === "professional_note") return;
    if (action.key === "timeline") return;
    if (action.key === "report") return;

    switch (action.key) {
      case "messages":
        navigate("/messages");
        break;
      case "routines":
        navigate(`/children/${child.id}/appointments`);
        break;
      case "notes":
        alert("Caregiver Notes: Navigate to notes management (to be implemented)");
        break;
      case "access":
        alert("Shared Access: Navigate to access management (to be implemented)");
        break;
      default:
        console.log(`Action ${action.key} not yet implemented`);
    }
  };

  const getTypeConfig = (type) => (
    getTimelineTypeConfig(type) || {
      icon: "📝",
      color: theme.palette.primary.main,
      label: type,
    }
  );

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const entryTime = new Date(timestamp);
    const diffMs = now - entryTime;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return "Just now";
  };

  return {
    formatTimeAgo,
    getTypeConfig,
    handleAddChild,
    handleAddChildSuccess,
    handleCreateCustomCategories,
    handleDailyReport,
    handleDeleteChild,
    handleEditChild,
    handleReviewChild,
    handleEditChildSuccess,
    handleGroupActionClick,
    handleInviteTeamMember,
    handleMessages,
    handleShowCareReport,
    refreshDashboard,
  };
};
